import type { LegacyApiError } from '@omnipresentgroup/api';
import {
  Button,
  notifyError,
  notifySuccess,
} from '@omnipresentgroup/design-system';
import { useBulkUpdateExpensesMutation } from '@omnipresentgroup/domains';
import {
  type BaseExpense,
  type BulkUpdateExpensesResponse,
  ExpenseStatusEnum,
} from '@omnipresentgroup/types';

export const ApproveExpensesAction = ({
  expenses,
  iconOnly = false,
  onSuccess,
}: {
  expenses: BaseExpense[];
  iconOnly?: boolean;
  onSuccess?: () => void;
}) => {
  const { mutate: approveExpenses, isPending: isApprovalPending } =
    useBulkUpdateExpensesMutation({
      onError: (error: LegacyApiError) => {
        notifyError({
          title: 'Error',
          description: `Something went wrong with the approval of the expenses: ${error.message}`,
        });
      },
      onSuccess: (response: BulkUpdateExpensesResponse) => {
        const failed = response.filter((entry) => !entry.success).length;
        const success = response.filter((entry) => entry.success).length;
        if (failed) {
          notifyError({
            title: 'Error',
            description: `${failed} expense(s) could not be approved`,
          });
        }
        if (success) {
          notifySuccess({
            title: 'Success',
            description: `${success} expense(s) approved`,
          });
        }
        onSuccess?.();
      },
    });
  return (
    <Button
      icon={isApprovalPending ? undefined : 'Check'}
      loading={isApprovalPending}
      disabled={
        expenses.length === 0 ||
        expenses.some(
          (expense) => expense.status === ExpenseStatusEnum.APPROVED,
        )
      }
      onClick={() =>
        approveExpenses({
          expenses: expenses.map((expense) => ({
            expenseId: expense.id,
            approved: true,
          })),
        })
      }
    >
      {iconOnly
        ? ''
        : `Approve ${expenses.length === 0 ? '' : expenses.length}`}
    </Button>
  );
};
